import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { FormikProps } from "formik";
import { useTypedDispatch } from "../../../app/state";
import { Grid, TextField, Typography } from "@material-ui/core";
import {
  selectTechnologyConfigurationListByCountryId
} from "../../../store/technologyConfigurationStore/technologyConfigurationSelectors";
import { selectCurrentLocation } from "../../../store/locationStore/locationSelectors";
import { Dropdown } from "../../../app/components/Dropdown";
import DatePicker from "../../../components/DatePicker";
import { selectCurrentLicence } from "../../../store/licenceStore/licenceSelectors";
import TechnologyChannelTable from "../TechnologyChannelTable";
import UserSelectionInput from "../../../components/UserSelectionInput";
import { getUserInfo } from "../../../store/helperStore/helperRequests";
import { selectAllHelperUsers } from "../../../store/helperStore/helperSelectors";

import classes from "./index.module.scss";
import Multiselect from "../../../components/Multiselect";

interface LicenceFormikInterface {
  name: string,
  technologyId: number,
  responsiblePeople: number[],
  issueDate: Date | null;
  expirationDate: Date | null;
  notifyInDays: number;
  description: string;
}

type LicenceFormProp = {
  isEditEnabled?: boolean,
  licenceFormik: FormikProps<LicenceFormikInterface>
}

const LicenceForm = ({ isEditEnabled = false, licenceFormik }: LicenceFormProp) => {
  const dispatch = useTypedDispatch();
  const currentLocation = useSelector(selectCurrentLocation);
  const currentLicence = useSelector(selectCurrentLicence);
  const allTechnologyConfigurations = useSelector(selectTechnologyConfigurationListByCountryId(currentLocation?.countryId));
  const allUserList = useSelector(selectAllHelperUsers);
  const licencedTechnologyList = allTechnologyConfigurations.filter((technology) => technology.isLicenced);

  const chosenTechnology = licenceFormik.values.technologyId
    ? licencedTechnologyList.find((technology) => technology.id === licenceFormik.values.technologyId) || currentLicence?.technology
    : null;

  const updateUserInfo = (userIdList: number[]) => {
    dispatch(getUserInfo(userIdList));
  }

  useEffect(() => {
    if (licenceFormik.values.responsiblePeople.length > 0) {
      updateUserInfo(licenceFormik.values.responsiblePeople);
    }
  }, [ licenceFormik.values.responsiblePeople ])


  const onChangeUser = (userIdList: number[]) => {
    licenceFormik.setFieldValue('responsiblePeople', userIdList);
  }

  return (
    <Grid
      container
      component='form'
      spacing={2}
      className={classes.form}
      wrap='wrap'
    >
      <Grid item xs={12}>
        <TextField
          label='Licence name'
          required
          variant='outlined'
          name='name'
          fullWidth
          disabled={isEditEnabled}
          value={licenceFormik.values.name ?? ''}
          onChange={licenceFormik.handleChange}
          helperText={licenceFormik.errors.name}
          error={Boolean(licenceFormik.errors.name)}
        />
      </Grid>
      <Grid item xs={6}>
        <Dropdown
          name='technologyId'
          label='Technology'
          fullWidth
          required
          disabled={isEditEnabled}
          selectedValue={licenceFormik.values?.technologyId}
          values={licencedTechnologyList}
          valueChange={licenceFormik.handleChange}
          error={licenceFormik.errors.technologyId}
        />
      </Grid>
      <Grid item xs={6}>
        <UserSelectionInput
          multiselect
          name='responsiblePersonId'
          label='Responsible Person'
          fullWidth
          disabled={isEditEnabled}
          items={Multiselect.getValues(allUserList, 'id', 'fullName')}
          selectedItems={Multiselect.getSelectedValues(allUserList, licenceFormik.values.responsiblePeople, 'id', 'fullName')}
          onMultiChange={onChangeUser}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant='h4'>Dates</Typography>
      </Grid>
      <Grid item xs={4}>
        <DatePicker
          disabled={isEditEnabled}
          variant='inline'
          inputVariant='outlined'
          label='Issue Date'
          name='issueDate'
          fullWidth
          value={licenceFormik.values?.issueDate}
          onChange={(value) => licenceFormik.setFieldValue('issueDate', value)}
          error={Boolean(licenceFormik.errors.issueDate)}
          helperText={licenceFormik.errors.issueDate}
        />
      </Grid>
      <Grid item xs={4}>
        <DatePicker
          disabled={isEditEnabled}
          variant='inline'
          inputVariant='outlined'
          label='Expiration Date'
          name='expirationDate'
          fullWidth
          value={licenceFormik.values?.expirationDate}
          onChange={(value) => licenceFormik.setFieldValue('expirationDate', value)}
          error={Boolean(licenceFormik.errors.expirationDate)}
          helperText={licenceFormik.errors.expirationDate}
        />
      </Grid>
      <Grid item xs={4}>
        <TextField
          label='Notification period, days'
          variant='outlined'
          name='notifyInDays'
          fullWidth
          disabled={isEditEnabled}
          value={licenceFormik.values.notifyInDays ?? ''}
          onChange={licenceFormik.handleChange}
          helperText={licenceFormik.errors.notifyInDays}
          error={Boolean(licenceFormik.errors.notifyInDays)}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          disabled={isEditEnabled}
          name='description'
          label='Licence description'
          multiline
          maxRows={3}
          onChange={licenceFormik.handleChange}
          value={licenceFormik.values?.description}
          variant='outlined'
          error={Boolean(licenceFormik.errors.description)}
          helperText={licenceFormik.errors.description}
        />
      </Grid>
      {
        chosenTechnology ? (
          <Grid item xs={12}>
            <Typography variant='h4'>Technology Channels Display</Typography>
          </Grid>
        ) : null
      }
      <TechnologyChannelTable technology={chosenTechnology} />
    </Grid>
  )
}

export default LicenceForm;